import { getActiveUser } from "@noom/hive-auth";
import Cookies from "js-cookie";
import { useRef } from "react";
import { useCallbackRef, useOnce } from "src/hooks/lifecycle";
import { trackEvent } from "src/utils/api/tracker";
import {
  getInAppAutoCookie,
  getWebAuthCookie,
} from "src/utils/authCookieParser";
import { captureException, captureMessageWithExtras } from "src/utils/error";

/**
 * Temporary debug information to see the authentication state.
 * TODO: Remove this in UPSELL-1608
 */
export function getAuthDebugInfo() {
  try {
    return {
      activeUserIsSet: !!getActiveUser(),
      currentDomain: window.location.hostname,
      hasHiveAuthCookie: !!Cookies.get("hive-auth"),
      hasUserIdCookie: !!Cookies.get("_userId"),
      autologinDataCookieSet: !!getInAppAutoCookie(),
      identityDataCookieSet: !!getWebAuthCookie(),
      hasHiveControlCookie: !!Cookies.get("hive-control"),
      hasHiveControlSession: !!sessionStorage.getItem("hive-control"),
      hasHiveControlLocal: !!localStorage.getItem("hive-control"),
      // Note: window.OneTrust is always set as empty stub by onetrust.tsx
      // Check for OnConsentChanged to verify OneTrust actually loaded
      hasOneTrustStub: !!(window as any).OneTrust,
      hasOneTrustLoaded:
        typeof (window as any).OneTrust?.OnConsentChanged === "function",
      onetrustActiveGroups: (window as any).OnetrustActiveGroups,
    };
  } catch (err) {
    captureException(err);
    return {
      debugError: err,
    };
  }
}

// TODO: Remove this after UPSELL-1608 is resolved
export function trackLoginCallbackSuccess() {
  try {
    Cookies.set("hive-control", "1");
    sessionStorage.setItem("hive-control", "1");
    localStorage.setItem("hive-control", "1");
    const activeUser = getActiveUser();
    trackEvent("BuyflowLoginCallbackSuccess", {
      userAccessCode: activeUser?.accessCode,
      ...getAuthDebugInfo(),
    });
  } catch (e) {
    captureException(e);
  }
}

/**
 * Hook to track when hive-* cookies are being mutated.
 * Intercepts document.cookie setter and cookieStore API to capture stack traces.
 * TODO: Remove this once UPSELL-1608 is resolved
 */
export function useTrackHiveCookieMutation() {
  const startedAt = useRef(Date.now()).current;

  const trackMutation = useCallbackRef(
    (cookieName: string | undefined, source: string) => {
      try {
        if (!cookieName?.startsWith("hive-")) {
          return;
        }

        const { stack } = new Error();
        const eventData = {
          cookieName,
          source,
          stack,
          startedAt,
          msSinceLoad: Date.now() - startedAt,
          ...getAuthDebugInfo(),
        };

        trackEvent("BuyflowHiveCookieMutation", eventData);
        captureMessageWithExtras(
          `Hive cookie mutation via ${source}: ${cookieName}`,
          eventData,
          ["BuyflowHiveCookieMutation"]
        );
      } catch (error) {
        captureException(error);
      }
    }
  );

  // Intercept document.cookie setter
  useOnce(() => {
    const originalCookieDescriptor = Object.getOwnPropertyDescriptor(
      Document.prototype,
      "cookie"
    );
    if (!originalCookieDescriptor?.set) {
      return undefined;
    }

    const originalSetter = originalCookieDescriptor.set;

    Object.defineProperty(document, "cookie", {
      ...originalCookieDescriptor,
      set(value: string) {
        try {
          const [rawCookieName] = value.split("=");
          trackMutation(rawCookieName?.trim(), "document.cookie");
        } catch (error) {
          captureException(error);
        }

        originalSetter.call(document, value);
      },
    });

    return () => {
      // Restore original cookie descriptor
      Object.defineProperty(document, "cookie", originalCookieDescriptor);
    };
  });

  // Intercept cookieStore.delete() and cookieStore.set()
  useOnce(() => {
    if (typeof cookieStore === "undefined") {
      return undefined;
    }

    const originalDelete = cookieStore.delete.bind(cookieStore);
    const originalSet = cookieStore.set.bind(cookieStore);

    cookieStore.delete = function deleteOverride(
      nameOrOptions: string | CookieStoreDeleteOptions
    ) {
      const cookieName =
        typeof nameOrOptions === "string" ? nameOrOptions : nameOrOptions?.name;
      trackMutation(cookieName, "cookieStore.delete");

      if (typeof nameOrOptions === "string") {
        return originalDelete(nameOrOptions);
      }
      return originalDelete(nameOrOptions);
    };

    cookieStore.set = function setOverride(
      nameOrOptions: string | CookieInit,
      value?: string
    ) {
      const cookieName =
        typeof nameOrOptions === "string" ? nameOrOptions : nameOrOptions?.name;
      trackMutation(cookieName, "cookieStore.set");

      if (typeof nameOrOptions === "string") {
        return originalSet(nameOrOptions, value ?? "");
      }
      return originalSet(nameOrOptions);
    };

    return () => {
      cookieStore.delete = originalDelete;
      cookieStore.set = originalSet;
    };
  });

  useOnce(() => {
    if (typeof cookieStore === "undefined") {
      return () => {};
    }

    const listener = (event: CookieChangeEvent) => {
      const { changed, deleted } = event;
      const hiveCookiesDeleted = deleted
        .filter((d) => d.name.startsWith("hive-"))
        .map((d) => d.name);
      const hiveCookiesChanged = changed
        .filter((c) => c.name.startsWith("hive-"))
        .map((c) => c.name);

      const trackAllCookieChanges = window.location.pathname.includes(
        "/payment/linkout/checkout"
      );
      if (
        trackAllCookieChanges ||
        hiveCookiesDeleted.length > 0 ||
        hiveCookiesChanged.length > 0
      ) {
        trackEvent("BuyflowCookieChange", {
          changed: changed.map((c) => c.name),
          deleted: deleted.map((d) => d.name),
          hiveCookiesDeleted,
          startedAt,
          msSinceLoad: Date.now() - startedAt,
          ...getAuthDebugInfo(),
        });
      }

      // If hive cookies were deleted, log recent network requests for correlation
      if (hiveCookiesDeleted.length > 0) {
        try {
          const recentRequests = performance
            .getEntriesByType?.("resource")
            ?.filter(
              (entry) =>
                Date.now() - performance.timeOrigin - entry.startTime < 5000
            )
            .slice(-20) // Limit to last 20 requests to avoid large payloads
            .map((entry) => {
              // Extract origin + pathname to avoid leaking sensitive query params
              let url: string;
              try {
                const parsed = new URL(entry.name);
                url = parsed.origin + parsed.pathname;
              } catch {
                url = entry.name;
              }
              return {
                url,
                startTime: Math.round(entry.startTime),
                duration: Math.round(
                  (entry as PerformanceResourceTiming).duration
                ),
              };
            });

          trackEvent("BuyflowHiveCookieDeletedWithNetworkContext", {
            hiveCookiesDeleted,
            recentRequests,
            startedAt,
            msSinceLoad: Date.now() - startedAt,
            ...getAuthDebugInfo(),
          });
          captureMessageWithExtras(
            "Hive cookie deleted with network context",
            {
              hiveCookiesDeleted,
              startedAt,
              msSinceLoad: Date.now() - startedAt,
              currentPath: window.location.pathname,
              ...getAuthDebugInfo(),
            },
            ["BuyflowHiveCookieDeletedWithNetworkContext"]
          );
        } catch (error) {
          captureException(error);
        }
      }
    };
    cookieStore.addEventListener("change", listener);
    return () => {
      cookieStore.removeEventListener("change", listener);
    };
  });
}
